import React, { useRef, useState } from "react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { ProseMarkdown } from "@/components/prose-markdown";
import { uploadAssetFile } from "@/lib/swr";
import { isHtmlFile } from "@/lib/assets";
import { useToast } from "@/hooks/use-toast";
import { toastError } from "@/lib/toast";

// Markdown textarea with paste/drop upload: image files from the clipboard
// or a drag-drop are uploaded (GCS-backed /uploads) and an `![](url)`
// reference is inserted at the caret; dropped `.html` files (AI-generated
// reports / prototypes) upload the same way and insert a `[name](url)` link
// that ProseMarkdown renders as a sandboxed preview. Plain text paste is
// untouched. Used by the PRD content editor and thread reply/edit boxes.
// `withPreview` adds a Write/Preview toggle (for hosts without their own,
// e.g. thread boxes; the PRD editor keeps its outer tabs).
export const MarkdownTextarea = ({
  value,
  onValueChange,
  withPreview,
  ...props
}: {
  value: string;
  onValueChange: (v: string) => void;
  withPreview?: boolean;
} & Omit<
  React.ComponentProps<typeof Textarea>,
  "value" | "onChange" | "onPaste" | "onDrop"
>) => {
  const { toast } = useToast();
  const ref = useRef<HTMLTextAreaElement>(null);
  const [uploading, setUploading] = useState(false);
  const [tab, setTab] = useState<"write" | "preview">("write");
  // Async uploads outlive a render — read the latest text through a ref so
  // sequential inserts don't clobber each other.
  const valueRef = useRef(value);
  valueRef.current = value;

  const insertAtCaret = (snippet: string) => {
    const el = ref.current;
    const text = valueRef.current;
    const pos = el ? el.selectionEnd : text.length;
    onValueChange(text.slice(0, pos) + snippet + text.slice(pos));
  };

  const handleFiles = async (files: File[]) => {
    setUploading(true);
    try {
      for (const file of files) {
        const { url } = await uploadAssetFile(file);
        insertAtCaret(
          isHtmlFile(file)
            ? `\n[${file.name}](${url})\n`
            : `\n![](${url})\n`
        );
      }
    } catch (e) {
      toastError(toast, "Upload failed", e);
    } finally {
      setUploading(false);
    }
  };

  const uploadableFiles = (list: FileList | null) =>
    Array.from(list ?? []).filter(
      (f) => f.type.startsWith("image/") || isHtmlFile(f)
    );

  // Clipboard shape varies by copy source: screenshots usually land in
  // `files`, images copied from apps/web pages often only appear as
  // `items` of kind "file" — check both.
  const imagesFromClipboard = (data: DataTransfer | null) => {
    const fromFiles = uploadableFiles(data?.files ?? null);
    if (fromFiles.length) return fromFiles;
    return Array.from(data?.items ?? [])
      .filter((i) => i.kind === "file" && i.type.startsWith("image/"))
      .map((i) => i.getAsFile())
      .filter((f): f is File => !!f);
  };

  const editor = (
    <Textarea
      ref={ref}
      value={value}
      onChange={(e) => onValueChange(e.target.value)}
      onPaste={(e) => {
        const files = imagesFromClipboard(e.clipboardData);
        if (files.length) {
          e.preventDefault();
          handleFiles(files);
        }
      }}
      onDrop={(e) => {
        const files = uploadableFiles(e.dataTransfer?.files ?? null);
        if (files.length) {
          e.preventDefault();
          handleFiles(files);
        }
      }}
      {...props}
    />
  );

  return (
    <div className="flex flex-col gap-1">
      {withPreview && (
        <Tabs
          value={tab}
          onValueChange={(v) => setTab(v as "write" | "preview")}
        >
          <TabsList>
            <TabsTrigger value="write">
              Write
            </TabsTrigger>
            <TabsTrigger value="preview">
              Preview
            </TabsTrigger>
          </TabsList>
        </Tabs>
      )}
      {withPreview && tab === "preview" ? (
        <div className="min-h-[80px] rounded-md border p-3">
          {value.trim() ? (
            <ProseMarkdown content={value} />
          ) : (
            <p className="text-sm text-muted-foreground">Nothing to preview.</p>
          )}
        </div>
      ) : (
        editor
      )}
      {uploading && (
        <span className="text-xs text-muted-foreground">Uploading…</span>
      )}
    </div>
  );
};
